import { useReservation } from '@/hooks/use-reservation';
import { fillMissingImageAlt } from '@/lib/image-alt';
import { scopeCss } from '@/lib/scope-css';
import { cn } from '@/lib/utils';
import { useModalStore } from '@/store/modal.store';
import { useEffect, useId, useMemo, useRef } from 'react';

// 좌표를 비율에 맞춰 조정하는 함수
const scaleCoords = (
  originalCoords: string, // 원본 좌표 (예: "100,200,300,400")
  originalWidth: number, // 원본 이미지 너비
  currentWidth: number, // 현재 이미지 너비
): string => {
  const coordsArray = originalCoords.split(',').map(Number);
  const ratio = currentWidth / originalWidth;
  return coordsArray.map((coord) => Math.round(coord * ratio)).join(',');
};

interface SafeHtmlRendererProps {
  html: string | undefined | null;
  className?: string;
  // 지정하면 alt가 없는 본문 이미지에 이 값(글 제목) 기반의 대체 텍스트를 채운다.
  imageAltFallback?: string;
}

const getNumericDimension = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number.parseFloat(value.replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// 1. 타입 가드 함수 사용
function isHTMLImageElement(element: Element): element is HTMLImageElement {
  return element.tagName === 'IMG';
}

/**
 * 콘텐츠가 완전한 HTML 문서(<html>)인지 여부.
 * - 완전 문서(개인정보·약관 등): 문서 전체 서식을 그대로 살리려고 Shadow DOM으로 렌더(CSR).
 * - 조각 HTML(블로그·이벤트·공지 등 SEO 대상): 라이트 DOM으로 SSR 렌더(크롤러 본문 노출).
 *   조각에 딸린 <style>은 scopeCss로 본문 컨테이너 안에 가둔다. 블로그 글 대부분이
 *   표 서식용 <style>을 갖고 있어, <style> 유무로 나누면 본문이 CSR로 빠진다.
 */
function needsShadowIsolation(html: string): boolean {
  return /<html[\s>]/i.test(html);
}

/** 본문 <h1>을 표시(data-article-h1)를 단 <h2>로 바꾼다 */
function demoteH1(html: string): string {
  return html
    .replace(/<h1(?=[\s>])/gi, '<h2 data-article-h1')
    .replace(/<\/h1\s*>/gi, '</h2>');
}

/**
 * 라이트 DOM 경로의 본문 HTML.
 * <body> 래퍼가 있으면 그 안쪽만 쓰고, <style>은 모아 scope 안으로 가둔 뒤 맨 앞에 둔다.
 * <script>는 뺀다 — Shadow DOM(innerHTML)에서는 실행되지 않던 것이 SSR HTML에서는
 * 실행되기 때문이다.
 */
function buildLightDomHtml(
  html: string,
  scope: string,
  imageAltFallback?: string,
): string {
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const content = (bodyMatch ? bodyMatch[1] : html).replace(
    /<script[\s\S]*?<\/script>/gi,
    '',
  );

  let css = '';
  const body = content.replace(
    /<style[^>]*>([\s\S]*?)<\/style>/gi,
    (_, styleContent: string) => {
      css += scopeCss(styleContent, scope);
      return '';
    },
  );

  // 본문 속 <h1>은 h2로 낮춘다 — 페이지 h1(글 제목)과 겹쳐 검색엔진이 'H1 2개 이상'으로 잡는다
  // (본문 맨 위에 제목을 h1로 한 번 더 쓴 글들, 2026-10-16). 크기는 globals.css 가 h1 그대로 그린다.
  const bodyWithHeadings = demoteH1(body);
  const bodyWithAlt = imageAltFallback
    ? fillMissingImageAlt(bodyWithHeadings, imageAltFallback)
    : bodyWithHeadings;

  return css ? `<style>${css}</style>${bodyWithAlt}` : bodyWithAlt;
}

// 반응형 미디어/이미지맵 후처리 로직. Shadow DOM·라이트 DOM 컨테이너 모두에서 동작한다.
function enhanceContent(
  root: ShadowRoot | HTMLElement,
  originalDimensions: Map<HTMLImageElement, { width: number; height: number }>,
): () => void {
  // 에디터 HTML에서 이미지가 p 태그로 감싸질 때 기본 문단 여백으로 인해
  // 이미지 사이에 불필요한 간격이 생기지 않도록 이미지 전용 문단만 여백 제거
  const imageOnlyParagraphs = root.querySelectorAll('p');
  imageOnlyParagraphs.forEach((paragraph) => {
    const childNodes = Array.from(paragraph.childNodes);
    const hasImage = childNodes.some(
      (node) => node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'IMG',
    );
    const isImageOnlyParagraph =
      hasImage &&
      childNodes.every((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent?.trim() === '';
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return false;
        }
        return node.nodeName === 'IMG' || node.nodeName === 'BR';
      });

    if (isImageOnlyParagraph) {
      (paragraph as HTMLElement).style.margin = '0';
      (paragraph as HTMLElement).style.lineHeight = '0';
    }
  });

  const responsiveMediaElements = root.querySelectorAll(
    'iframe, video, embed, object',
  );
  responsiveMediaElements.forEach((element) => {
    const mediaElement = element as HTMLElement;
    const width = getNumericDimension(mediaElement.getAttribute('width'));
    const height = getNumericDimension(mediaElement.getAttribute('height'));
    if (width && height) {
      mediaElement.style.aspectRatio = `${width} / ${height}`;
    }
  });

  // 이미지맵 하나씩 처리
  const processSingleImageMap = (img: HTMLImageElement) => {
    const mapName = img.getAttribute('usemap')?.substring(1);
    if (!mapName) return;

    const map = root.querySelector(
      `map[name="${mapName}"]`,
    ) as HTMLMapElement | null;
    if (!map) return;

    const updateCoords = () => {
      if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
        requestAnimationFrame(updateCoords);
        return;
      }
      const currentWidth = img.clientWidth;
      if (currentWidth === 0) {
        requestAnimationFrame(updateCoords);
        return;
      }
      if (!originalDimensions.has(img)) {
        originalDimensions.set(img, {
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
      const { width: originalWidth } = originalDimensions.get(img)!;
      Array.from(map.getElementsByTagName('area')).forEach((area) => {
        const originalCoords =
          area.dataset.originalCoords || area.getAttribute('coords')!;
        if (!originalCoords) return;
        area.dataset.originalCoords = originalCoords;
        if (originalWidth > 0 && currentWidth > 0) {
          area.coords = scaleCoords(
            originalCoords,
            originalWidth,
            currentWidth,
          );
        }
      });
    };
    requestAnimationFrame(updateCoords);
  };

  // 이미지맵 초기 처리
  root.querySelectorAll('img[usemap]').forEach((element) => {
    if (!isHTMLImageElement(element)) return;
    const img = element;
    if (!img.complete) {
      const loadHandler = () => {
        processSingleImageMap(img);
        img.removeEventListener('load', loadHandler);
      };
      img.addEventListener('load', loadHandler);
    } else {
      processSingleImageMap(img);
    }
  });

  // 이미지 크기 변경 감지
  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const img = entry.target as HTMLImageElement;
      if (img.tagName === 'IMG' && img.hasAttribute('usemap')) {
        requestAnimationFrame(() => processSingleImageMap(img));
      }
    });
  });
  requestAnimationFrame(() => {
    root.querySelectorAll('img[usemap]').forEach((img) => {
      resizeObserver.observe(img);
    });
  });

  return () => {
    resizeObserver.disconnect();
    originalDimensions.clear();
  };
}

export default function SafeHtmlRenderer({
  html,
  className,
  imageAltFallback,
}: SafeHtmlRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // 글 전용 <style>을 가둘 범위. 서버·클라이언트가 같은 값을 내야 하이드레이션이 맞는다.
  const scopeId = useId();
  const originalDimensions = useRef<
    Map<HTMLImageElement, { width: number; height: number }>
  >(new Map());

  // 완전 문서면 Shadow DOM 격리 경로, 아니면 라이트 DOM SSR 경로.
  const useShadow = useMemo(
    () => (html ? needsShadowIsolation(html) : false),
    [html],
  );
  // 라이트 DOM 경로에서만 서버·클라이언트 동일 계산되어 SSR HTML에 본문이 렌더된다.
  const bodyHtml = useMemo(
    () =>
      html && !useShadow
        ? buildLightDomHtml(
            html,
            `[data-article-scope="${scopeId}"]`,
            imageAltFallback,
          )
        : '',
    [html, useShadow, scopeId, imageAltFallback],
  );

  // __html 객체를 고정한다. React 19는 이 prop을 객체 동일성으로 비교해, 렌더마다 새 객체를
  // 넘기면 다른 이유(배너 로드로 className 변경 등)로 다시 렌더될 때마다 innerHTML을 다시 쓴다.
  // 그러면 enhanceContent가 적용한 후처리(이미지 문단 여백·이미지맵 좌표 등)가 사라진다.
  const innerHtml = useMemo(
    () => (useShadow ? undefined : { __html: bodyHtml }),
    [useShadow, bodyHtml],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!html || !container) return;

    if (useShadow) {
      // ===== Shadow DOM 경로: 임베디드 <style>을 격리 적용(서식 보존) =====
      const shadowRoot =
        container.shadowRoot || container.attachShadow({ mode: 'open' });

      const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
      const styleContent = styleMatch ? styleMatch[0] : '';
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const bodyContent = bodyMatch ? bodyMatch[1] : html;

      shadowRoot.innerHTML = `${styleContent}
        <style>
          img { display: block; width: 100%; height: auto; margin: 0; }
          figure { margin: 0; }
          iframe, video, embed, object { max-width: 100% !important; }
          iframe, video { width: 100% !important; height: auto !important; }
        </style>
        ${bodyContent}`;

      const cleanup = enhanceContent(shadowRoot, originalDimensions.current);
      return () => {
        cleanup();
        shadowRoot.innerHTML = '';
      };
    }

    // ===== 라이트 DOM 경로: 본문은 dangerouslySetInnerHTML로 이미 SSR됨 =====
    return enhanceContent(container, originalDimensions.current);
  }, [html, useShadow, bodyHtml]);

  const { open: openReserveModal } = useModalStore();
  useReservation(openReserveModal, 'openReserveModal');

  return (
    <div
      ref={containerRef}
      data-article-scope={scopeId}
      className={cn('article-content', className)}
      dangerouslySetInnerHTML={innerHtml}
    />
  );
}
